// Size chart helpers — extracted from product.js so they are available on every
// page (product detail, wishlist, and any grid that renders the quick-add drawer).
// Exposes window.sizeChartHelpers; product.js consumes the same global.

// Normalize size label for comparison (trim, lowercase)
const normalizeSizeLabel = (str) => (str || "").trim().toLowerCase();

// Normalize a measure column header for keyword matching (strip accents/case)
const normalizeMeasureLabel = (str) =>
  (str || "")
    .normalize("NFD")
    .replace(/\p{Diacritic}+/gu, "")
    .toLowerCase();

const MEASURE_KEYWORDS = {
  pecho: ["pecho", "busto", "bust", "chest", "brust"],
  cintura: ["cintura", "waist", "taille"],
  cadera: ["cadera", "hip", "hufte"],
  longitud: ["longitud", "length", "largo", "lange"],
};

// Current-locale words for each measure, injected from the theme's own
// translation keys (window.sizeChartStrings.measureKeywords, set in
// head-init.liquid) so matching works for any storefront language, not just
// the keywords hardcoded above.
const localeMeasureKeyword = (key) => {
  const localized = window.sizeChartStrings?.measureKeywords?.[key];
  return localized ? normalizeMeasureLabel(localized) : null;
};

const matchMeasureKey = (headerText) => {
  const normalized = normalizeMeasureLabel(headerText);
  return (
    Object.keys(MEASURE_KEYWORDS).find((key) => {
      const localeKeyword = localeMeasureKeyword(key);
      return (
        (localeKeyword && normalized.includes(localeKeyword)) ||
        MEASURE_KEYWORDS[key].some((keyword) => normalized.includes(keyword))
      );
    }) || null
  );
};

// Diagram callouts keyed by size id, populated by buildSizeChartPanesFromSource
// and read by setActive() so the arrows update when the size tab changes.
//
// Va por ámbito y no en un único par de variables de módulo: en una ficha de
// producto pueden convivir la guía de la propia PDP y la que inyecta el
// quick-add con otro producto (assets/quick-add-cart.js), y con estado
// compartido la segunda pisaba las medidas de la primera.
const chartStateFor = (scope) => {
  if (!scope.__sizeChartState) {
    scope.__sizeChartState = { measureNames: [], valuesById: new Map() };
  }
  return scope.__sizeChartState;
};

// Becomes true once the user actually opens the size-guide modal for the
// first time (see initSizeGuideDiagramAnimation's click handler). Before
// that, updateDiagramCallouts() only populates data — it never reveals —
// so nothing animates while the page is still loading.
let diagramRevealEnabled = false;

// Milliseconds per character for the typewriter effect on the callout
// label/value, and the duration of the line-grow CSS animation in
// _modal.scss (size-guide-line-grow) — the typing starts only once the line
// has fully settled, so kept in sync with that animation's declared 1s.
const TYPE_CHAR_MS = 60;
const LINE_GROW_MS = 1000;

const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Types `text` into `el` one character at a time. Resolves once done, so
// callers can chain a label typing into a value typing.
const typeText = (el, text, speed = TYPE_CHAR_MS) =>
  new Promise((resolve) => {
    if (!el) {
      resolve();
      return;
    }
    if (prefersReducedMotion() || !text) {
      el.textContent = text || "";
      resolve();
      return;
    }
    let i = 0;
    const tick = () => {
      i += 1;
      el.textContent = text.slice(0, i);
      if (i < text.length) setTimeout(tick, speed);
      else resolve();
    };
    tick();
  });

// Erases `el`'s current text one character at a time (used before retyping
// a changed measurement value on size change).
const eraseText = (el, speed = TYPE_CHAR_MS) =>
  new Promise((resolve) => {
    const text = el?.textContent || "";
    if (!el || prefersReducedMotion() || !text) {
      if (el) el.textContent = "";
      resolve();
      return;
    }
    let i = text.length;
    const tick = () => {
      i -= 1;
      el.textContent = text.slice(0, i);
      if (i > 0) setTimeout(tick, speed);
      else resolve();
    };
    tick();
  });

// Restarts the line-grow CSS animation on a callout (removes/re-adds
// .is-revealed with a forced reflow in between). Reading offsetWidth forces
// a synchronous style/layout flush between removing and re-adding the
// class — without it, the browser can coalesce both mutations into a single
// style recalculation and skip restarting the CSS animation.
const replayCalloutLine = (callout) => {
  const lineEl = callout.querySelector(".size-guide-modal__callout-line");
  callout.classList.remove("is-revealed");
  if (lineEl) lineEl.style.animation = "none";
  callout.offsetWidth;
  if (lineEl) lineEl.style.animation = "";
  callout.classList.add("is-revealed");
};

// Reveals a callout for the first time (line grows, then label and number
// are typed in sequentially, unit last) or, if it was already visible and
// its number changed on a size switch, replays the line and retypes just the
// number — the label ("Cintura", "Pecho"...) and unit ("cm") don't change
// between sizes, only the numeric value does.
const revealOrUpdateCallout = (
  callout,
  labelEl,
  numberEl,
  unitEl,
  newNumberText,
  unitText,
) => {
  const isFirstReveal = !callout.classList.contains("is-revealed");
  const numberChanged = numberEl.dataset.currentValue !== newNumberText;
  numberEl.dataset.currentValue = newNumberText;

  if (!isFirstReveal && !numberChanged) return;

  if (!isFirstReveal) {
    eraseText(numberEl).then(() => typeText(numberEl, newNumberText));
    return;
  }

  if (!labelEl.dataset.fullText) {
    labelEl.dataset.fullText = labelEl.textContent;
  }

  callout.classList.remove("is-text-revealed");
  replayCalloutLine(callout);

  setTimeout(() => {
    callout.classList.add("is-text-revealed");
    labelEl.textContent = "";
    numberEl.textContent = "";
    unitEl.textContent = unitText;
    typeText(labelEl, labelEl.dataset.fullText).then(() =>
      typeText(numberEl, newNumberText),
    );
  }, LINE_GROW_MS);
};

// Reveals/fills .size-guide-modal__callout elements that match a measure
// column for the given size's row values; hides the rest.
// `reveal: false` only unhides the callout and stores its data (fullText /
// currentValue) without touching is-revealed or starting the line/typing
// animation — used at build time (page load) so the diagram stays inert
// until the user actually opens the modal (see initSizeGuideDiagramAnimation).
const updateDiagramCallouts = (
  measureNames,
  values,
  { reveal = diagramRevealEnabled, scope = document } = {},
) => {
  const callouts = scope.querySelectorAll(
    ".size-guide-modal__callout[data-measure]",
  );
  if (!callouts.length) return;

  callouts.forEach((callout) => {
    callout.hidden = true;
  });

  measureNames.forEach((rawMeasure, i) => {
    const key = matchMeasureKey(rawMeasure);
    const val = values[i];
    if (!key || !val) return;

    const callout = scope.querySelector(
      `.size-guide-modal__callout[data-measure="${key}"]`,
    );
    const labelEl = callout?.querySelector(".size-guide-modal__callout-label");
    const numberEl = callout?.querySelector("[data-measure-number]");
    const unitEl = callout?.querySelector("[data-measure-unit]");
    if (!callout || !numberEl || !unitEl || !labelEl) return;

    const unitMatch = rawMeasure.match(/\(([^)]+)\)\s*$/);
    const unitText = unitMatch ? ` ${unitMatch[1]}` : "";

    callout.hidden = false;

    if (!reveal) {
      if (!labelEl.dataset.fullText) {
        labelEl.dataset.fullText = labelEl.textContent;
      }
      numberEl.dataset.currentValue = val;
      unitEl.dataset.unitText = unitText;
      unitEl.textContent = unitText;
      return;
    }

    revealOrUpdateCallout(callout, labelEl, numberEl, unitEl, val, unitText);
  });
};

// Runs the first-reveal animation (line grow + label/number typing, unit
// written instantly) for callouts already populated with data by
// updateDiagramCallouts(reveal: false) at build time. Triggered from the
// "Guía de tallas" opener click (see initSizeGuideDiagramAnimation) instead
// of at page load, so the diagram stays inert until the user actually opens
// the modal.
const replaySizeGuideDiagramAnimation = (scope) => {
  scope
    .querySelectorAll(".size-guide-modal__callout:not([hidden])")
    .forEach((callout) => {
      const labelEl = callout.querySelector(".size-guide-modal__callout-label");
      const numberEl = callout.querySelector("[data-measure-number]");
      const unitEl = callout.querySelector("[data-measure-unit]");
      const fullLabel = labelEl?.dataset.fullText || labelEl?.textContent || "";
      const fullNumber = numberEl?.dataset.currentValue || numberEl?.textContent || "";
      const unitText = unitEl?.dataset.unitText || unitEl?.textContent || "";

      callout.classList.remove("is-text-revealed");
      replayCalloutLine(callout);

      setTimeout(() => {
        callout.classList.add("is-text-revealed");
        if (labelEl) labelEl.textContent = "";
        if (numberEl) numberEl.textContent = "";
        if (unitEl) unitEl.textContent = unitText;
        if (labelEl) {
          typeText(labelEl, fullLabel).then(
            () => numberEl && typeText(numberEl, fullNumber),
          );
        }
      }, LINE_GROW_MS);
    });
};

// Moves the page's single #naiz-opener-container (populated by the NaizFit
// script) into this modal's footer slot — same relocation pattern as
// component-sheet-size.liquid's relocateNaizContainer(). The modal overlay
// covers main-product.liquid's own "next to the size-guide opener" slot on
// both desktop and mobile, so the CTA would be unreachable while the guide is
// open unless it's moved in here too.
const relocateNaizContainerIntoSizeGuide = (scope) => {
  const target = scope.querySelector("[data-naizfit-ui]");
  const container = document.getElementById("naiz-opener-container");
  if (!target || !container) return;

  // Remember where the container lives on the page itself, so it can be
  // moved back once the guide closes instead of staying stranded in a
  // detached/hidden modal.
  if (!container._naizHomeParent) container._naizHomeParent = container.parentElement;

  target.appendChild(container);
};

// Restore #naiz-opener-container to its page slot (next to the size-guide
// opener in main-product.liquid) once the modal that borrowed it closes.
document.body.addEventListener("modalClosed", () => {
  const container = document.getElementById("naiz-opener-container");
  if (!container || !container._naizHomeParent) return;
  if (container.closest(".brn-modal--size-guide")) {
    container._naizHomeParent.appendChild(container);
  }
});

const initSizeGuideDiagramAnimation = () => {
  document.querySelectorAll("[data-size-guide-trigger]").forEach((btn) => {
    if (btn.dataset.sizeGuideAnimationBound) return;
    btn.dataset.sizeGuideAnimationBound = "true";

    btn.addEventListener("click", () => {
      diagramRevealEnabled = true;

      const modalSelector = btn
        .closest("modal-opener")
        ?.getAttribute("data-modal");
      const scope = (modalSelector && document.querySelector(modalSelector)) || document;

      relocateNaizContainerIntoSizeGuide(scope);

      // Lifts the CSS gate (.size-guide-modal__diagram--visible in
      // _modal.scss) so callouts populated with reveal:false can actually
      // become visible — kept in lockstep with diagramRevealEnabled above.
      scope
        .querySelectorAll(".size-guide-modal__diagram")
        .forEach((diagram) => diagram.classList.add("size-guide-modal__diagram--visible"));

      // Only the first time this modal instance is opened — later opens
      // leave the diagram as the user left it (tab switches keep updating
      // it via setActive, now that diagramRevealEnabled is true).
      if (scope.dataset.sizeGuideDiagramAnimated) return;
      scope.dataset.sizeGuideDiagramAnimated = "true";

      // Wait for the sliding side-panel to finish its .4s open transition,
      // plus a bit more, so the line-drawing reads as a deliberate beat
      // after the panel settles instead of starting almost immediately.
      setTimeout(() => replaySizeGuideDiagramAnimation(scope), 600);
    });
  });
};

// Modal size chart table tabs.
// `root` acota la búsqueda a un modal concreto. Sin él se coge el primer
// .size-chart del documento, que en cualquier página con artículos en la cesta
// es el del sheet de tallas del carrito (snippets/cart-item.liquid) y no el que
// acaba de inyectarse.
const initSizeChartTabs = (root) => {
  const scope = root || document;
  const tabs = Array.from(scope.querySelectorAll(".size-chart__tab"));
  const panes = Array.from(scope.querySelectorAll(".size-chart__pane"));

  if (!tabs.length || !panes.length) {
    return;
  }

  const setActive = (tabId) => {
    tabs.forEach((tab) => {
      const isActive = tab.id === tabId;
      tab.classList.toggle("active", isActive);
      tab.setAttribute("aria-selected", String(isActive));
      tab.setAttribute("tabindex", isActive ? "0" : "-1");
    });

    panes.forEach((pane) => {
      const isActive = pane.id === `size-${tabId}`;
      pane.classList.toggle("is-active", isActive);
      pane.hidden = !isActive;
      pane.setAttribute("aria-hidden", String(!isActive));
    });

    const { measureNames, valuesById } = chartStateFor(scope);
    const values = valuesById.get(tabId);
    if (values) updateDiagramCallouts(measureNames, values, { scope });
  };

  tabs.forEach((tab) => {
    tab.addEventListener("click", () => setActive(tab.id));

    tab.addEventListener("keydown", (event) => {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") {
        return;
      }

      event.preventDefault();
      const currentIndex = tabs.indexOf(tab);
      const direction = event.key === "ArrowLeft" ? -1 : 1;
      const nextIndex = (currentIndex + direction + tabs.length) % tabs.length;
      const nextTab = tabs[nextIndex];
      setActive(nextTab.id);
      nextTab.focus();
    });
  });

  const initialTab =
    tabs.find((tab) => tab.classList.contains("active")) || tabs[0];
  if (initialTab) {
    setActive(initialTab.id);
  }
};

// Build size chart tabs from the source table inside [data-size-guide-source]
// Reads sizes from tbody th and generates .size-chart__tab buttons
// If data-product-sizes is set, only shows sizes that the product has
const initSizeChartTabsFromSource = (root) => {
  const scope = root || document;
  const modal = scope.querySelector(".size-chart");
  const sourceContainer =
    (modal && modal.querySelector("[data-size-guide-source]")) ||
    scope.querySelector("[data-size-guide-source]");
  const tabsContainer =
    (modal && modal.querySelector(".size-chart__tabs")) ||
    scope.querySelector(".size-chart__tabs");

  if (!sourceContainer || !tabsContainer) return;

  const table = sourceContainer.querySelector("table");
  const tbody = table ? table.querySelector("tbody") : null;
  if (!table || !tbody) return;

  const rows = Array.from(tbody.querySelectorAll("tr"));
  if (!rows.length) return;

  const productSizesRaw = modal?.dataset?.productSizes || "";
  const productSizesSet = productSizesRaw
    ? new Set(
      productSizesRaw.split("|").map(normalizeSizeLabel).filter(Boolean),
    )
    : null;

  // Extract sizes from first th in each row, preserve order, dedupe
  // Filter to product sizes only when data-product-sizes is present
  const seen = new Set();
  const sizes = [];
  rows.forEach((row) => {
    const th = row.querySelector("th");
    const label = th ? th.textContent.trim() : "";
    if (!label || seen.has(label)) return;
    if (productSizesSet && !productSizesSet.has(normalizeSizeLabel(label)))
      return;
    seen.add(label);
    sizes.push(label);
  });

  if (!sizes.length) return;

  // Helper to create safe id from label
  const toId = (str) => {
    return str
      .toLowerCase()
      .normalize("NFD")
      .replace(/\p{Diacritic}+/gu, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  // Build buttons efficiently
  tabsContainer.innerHTML = "";
  const frag = document.createDocumentFragment();
  sizes.forEach((label, idx) => {
    const id = toId(label) || `size-${idx + 1}`;
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "size-chart__tab";
    btn.id = id;
    btn.setAttribute("role", "tab");
    btn.setAttribute("aria-controls", `size-${id}`);
    btn.setAttribute("aria-selected", idx === 0 ? "true" : "false");
    btn.setAttribute("tabindex", idx === 0 ? "0" : "-1");
    if (idx === 0) btn.classList.add("active");
    btn.textContent = label;
    frag.appendChild(btn);
  });

  tabsContainer.appendChild(frag);
};

// Build size chart panes from the source table inside [data-size-guide-source]
// Creates one pane per size with a table of measurements
// If data-product-sizes is set, only includes panes for sizes the product has
const buildSizeChartPanesFromSource = (root) => {
  const scope = root || document;
  const modal = scope.querySelector(".size-chart");
  const sourceContainer =
    (modal && modal.querySelector("[data-size-guide-source]")) ||
    scope.querySelector("[data-size-guide-source]");
  const contentContainer =
    (modal && modal.querySelector(".size-chart__content")) ||
    scope.querySelector(".size-chart__content");

  if (!contentContainer) return;

  // Always clear content to remove any hardcoded pane
  contentContainer.innerHTML = "";

  const table = sourceContainer?.querySelector("table");
  const tbody = table?.querySelector("tbody");
  if (!tbody) return;

  const thead = table.querySelector("thead");
  const headerCells = thead
    ? Array.from(thead.querySelectorAll("tr th, tr td"))
    : [];

  // Determine measurement labels: prioritize table headers (from Shopify Admin Page)
  // over themes fixed translations (window.sizeChartStrings.measures)
  const tableHeaders =
    headerCells.length > 1
      ? headerCells
        .slice(1)
        .map((cell) => cell.textContent.trim())
        .filter((h) => h !== "")
      : [];

  const measureNames =
    tableHeaders.length > 0
      ? tableHeaders
      : window.sizeChartStrings?.measures || [];

  let rows = Array.from(tbody.querySelectorAll("tr"));
  const productSizesRaw = modal?.dataset?.productSizes || "";
  const productSizesSet = productSizesRaw
    ? new Set(
      productSizesRaw.split("|").map(normalizeSizeLabel).filter(Boolean),
    )
    : null;
  if (productSizesSet) {
    rows = rows.filter((row) => {
      const label = (row.querySelector("th")?.textContent || "").trim();
      return productSizesSet.has(normalizeSizeLabel(label));
    });
  }
  if (!rows.length) return;

  const toId = (str) => {
    return str
      .toLowerCase()
      .normalize("NFD")
      .replace(/\p{Diacritic}+/gu, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  const frag = document.createDocumentFragment();

  const state = chartStateFor(scope);
  state.measureNames = measureNames;
  state.valuesById.clear();

  rows.forEach((row, idx) => {
    const sizeLabel = (row.querySelector("th")?.textContent || "").trim();
    const id = toId(sizeLabel) || `size-${idx + 1}`;
    const values = Array.from(row.querySelectorAll("td")).map((td) =>
      td.textContent.trim(),
    );

    state.valuesById.set(id, values);
    if (idx === 0) updateDiagramCallouts(measureNames, values, { reveal: false, scope });

    const pane = document.createElement("div");
    pane.className = "size-chart__pane";
    pane.id = `size-${id}`;
    pane.setAttribute("role", "tabpanel");
    pane.setAttribute("aria-labelledby", id);
    const isActive = idx === 0;
    pane.classList.toggle("is-active", isActive);
    pane.hidden = !isActive;
    pane.setAttribute("aria-hidden", String(!isActive));

    const t = document.createElement("table");
    const body = document.createElement("tbody");

    values.forEach((val, i) => {
      const tr = document.createElement("tr");
      const th = document.createElement("th");

      // Extract unit from measure name, e.g. "A: Pecho (cm)" → label "A: Pecho", unit "cm"
      const rawMeasure = measureNames[i] || "";
      const unitMatch = rawMeasure.match(/\(([^)]+)\)\s*$/);
      const unit = unitMatch ? unitMatch[1] : "";
      const cleanLabel = unitMatch
        ? rawMeasure.slice(0, unitMatch.index).trimEnd()
        : rawMeasure;

      th.textContent = cleanLabel.replace(":", " -");
      const td = document.createElement("td");
      td.textContent = unit ? `${val} ${unit}` : val;
      tr.appendChild(th);
      tr.appendChild(td);
      body.appendChild(tr);
    });

    t.appendChild(body);
    pane.appendChild(t);
    frag.appendChild(pane);
  });

  contentContainer.appendChild(frag);
};

window.sizeChartHelpers = {
  initSizeChartTabsFromSource,
  buildSizeChartPanesFromSource,
  initSizeChartTabs,
  initSizeGuideDiagramAnimation,
};
